import { Button, Form, Input, Select, Space } from "antd";
import { useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import FileIcon from "../../../assets/icons/FileIcon";
import SelectDropdownLgIcon from "../../../assets/icons/SelectDropdownLgIcon";
import { bankList } from "../../../constants/constants";
import {
  useGetEmployeeDetailsQuery,
  useUpdateEmployeeFinancialDataMutation,
} from "../../../features/employee/employeeApi";
import useError from "../../../hooks/useError";
import { toast } from "../../../lib/toast";
import { selectSearchOption } from "../../../utils/utils";
import InfoTitle from "../../common/InfoTitle";
import EmployeeFileUpload from "../EmployeeFileUpload";
import { replaceEditWithView } from "./utils";

export default function FinancialInfo() {
  const navigate = useNavigate();
  const location = useLocation();
  const [form] = Form.useForm();
  const { editId } = useParams();
  const {
    isError: isGetEmployeeDetailsError,
    error: getEmployeeDetailsError,
    data: getEmployeeDetails,
  } = useGetEmployeeDetailsQuery(editId || "", {
    skip: !editId,
  });
  const [
    updateEmployeeFinancialData,
    { isLoading, isError, error, isSuccess },
  ] = useUpdateEmployeeFinancialDataMutation();
  useError(
    isGetEmployeeDetailsError,
    getEmployeeDetailsError,
    "Get employee data fail"
  );
  useError(isError, error, "Update financial information fail");

  const employeeDetails = getEmployeeDetails?.data?.employee;

  const onFinish = (values: any) => {
    const newData = {
      ...values,
      bank_name: values.bank_name?.toLowerCase() || null,
      business_member_id: Number(editId),
      member_id: employeeDetails?.member_id,
      profile_id: employeeDetails?.member?.profile?.id,
      account_no: values?.account_no || null,
    };

    // console.log("NewData", newData);

    if (editId)
      updateEmployeeFinancialData({
        body: newData,
        id: editId,
      });
  };

  useEffect(() => {
    if (Object.keys(employeeDetails || {})) {
      form.setFieldsValue({
        tin_no: employeeDetails?.member?.profile?.tin_no,
        tin_certificate: employeeDetails?.member?.profile?.tin_certificate,
        bank_name: employeeDetails?.member?.profile?.bank_info?.bank_name,
        bank_account: employeeDetails?.member?.profile?.bank_info?.account_no,
        bank_account_title:
          employeeDetails?.member?.profile?.bank_info?.bank_account_title,
        branch_name: employeeDetails?.member?.profile?.bank_info?.branch_name,
        routing_no: employeeDetails?.member?.profile?.bank_info?.routing_no,
        account_no: employeeDetails?.bkash_info?.account_no,
      });
    }
  }, [form, employeeDetails]);

  const backLink = replaceEditWithView(location.pathname + location.search);

  // success toast message
  useEffect(() => {
    if (isSuccess) {
      toast("success", "Financial Information updated successfully!");
      navigate(backLink);
    }
  }, [backLink, isSuccess, navigate]);

  return (
    <div>
      <InfoTitle className="mb-6" icon={<FileIcon />}>
        Financial Information
      </InfoTitle>
      <Form
        className="custom-form"
        layout="vertical"
        requiredMark="optional"
        size="large"
        onFinish={onFinish}
        form={form}
      >
        <Form.Item
          name="tin_no"
          label="TIN Number"
          className="mb-4"
          rules={[
            {
              validator: async (_: any, value: string) => {
                if (!value) {
                  return Promise.resolve();
                }
                if (!value) {
                  return Promise.reject(new Error("Please enter TIN number"));
                }

                if (value.length < 12) {
                  return Promise.reject(
                    new Error("TIN number should be minimum 12")
                  );
                }

                if (value.length > 12) {
                  return Promise.reject(
                    new Error("TIN number should be maximum 12")
                  );
                }

                if (value.startsWith("-")) {
                  return Promise.reject(
                    new Error("TIN Number cannot be negative")
                  );
                }

                const regex = /^[A-Z0-9-]{5,20}$/;
                if (!regex.test(value)) {
                  return Promise.reject(
                    new Error("Please enter a valid TIN number")
                  );
                }
              },
            },
          ]}
        >
          <Input size="large" placeholder="Ex: 12345" />
        </Form.Item>
        <Form.Item
          name="tin_certificate"
          label="TIN Certificate Copy"
          className="mb-4"
          valuePropName="fileUrl"
          // rules={[
          //   {
          //     required: true,
          //     message: "Please upload TIN photo",
          //   },
          // ]}
        >
          <EmployeeFileUpload
            form={form}
            accept=".png, .jpg, .jpeg"
            name="tin_certificate"
            onChange={(url) =>
              form.setFieldsValue({
                tin_certificate: url,
              })
            }
          />
        </Form.Item>

        <Form.Item
          name="bank_name"
          label="Bank Name"
          className="mb-4"
          rules={[
            {
              required: false,
              message: "Please select bank name",
            },
          ]}
        >
          <Select
            placeholder="Select"
            size="large"
            allowClear
            showSearch
            filterOption={selectSearchOption}
            options={bankList}
            suffixIcon={<SelectDropdownLgIcon />}
          />
        </Form.Item>
        <Form.Item
          name="bank_account"
          label="Bank Account Number"
          className="mb-4"
          rules={
            editId
              ? [] // No validation rules when editing
              : [
                  {
                    validator: async (_: any, value: string) => {
                      if (!value) {
                        return Promise.reject(
                          new Error("Please enter bank account number")
                        );
                      }

                      if (value?.length < 6) {
                        return Promise.reject(
                          new Error("Bank account number should be minimum 06")
                        );
                      }

                      if (value?.length > 19) {
                        return Promise.reject(
                          new Error("Bank account number should be maximum 19")
                        );
                      }

                      if (value.startsWith("-")) {
                        return Promise.reject(
                          new Error("Bank account cannot be negative")
                        );
                      }

                      if (!Number.isInteger(Number(value))) {
                        return Promise.reject(
                          new Error("Bank account number must be a number")
                        );
                      }

                      const regex = /^[A-Za-z0-9-]{6,34}$/;
                      if (!regex.test(value)) {
                        return Promise.reject(
                          new Error("Please enter a valid bank account number")
                        );
                      }
                    },
                  },
                ]
          }
        >
          <Input size="large" placeholder="Ex: 12345" />
        </Form.Item>
        <Form.Item
          name="bank_account_title"
          label="Bank Account Title"
          className="mb-4"
        >
          <Input size="large" placeholder="Ex: Title" />
        </Form.Item>
        <Form.Item name="branch_name" label="Branch Name" className="mb-4">
          <Input size="large" placeholder="Ex: Branch" />
        </Form.Item>
        <Form.Item name="routing_no" label="Routing No" className="mb-4">
          <Input size="large" placeholder="Ex: 123654" />
        </Form.Item>

        <Form.Item
          name="account_no"
          label="Spay Wallet"
          // label="bKash Number"
          className="mb-4"
          rules={[
            {
              validator: async (_: any, value: string) => {
                if (!value) {
                  return Promise.resolve();
                }
                // Accept with or without +88
                const phoneRegex = /^(?:\+?88)?(01[3-9])[0-9]{8}$/;
                if (!phoneRegex.test(value)) {
                  return Promise.reject(
                    new Error("Please enter a valid Spay wallet number")
                  );
                }
              },
            },
          ]}
        >
          <Input size="large" placeholder="Ex: 8801" />
        </Form.Item>

        <Space className="w-full mt-2" size={12}>
          <Button
            className="normal-dark-button modal-button"
            onClick={() => navigate(backLink)}
            size="middle"
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            className="modal-button"
            type="primary"
            htmlType="submit"
            size="middle"
            loading={isLoading}
          >
            Update
          </Button>
        </Space>
      </Form>
    </div>
  );
}
